import { fetchFlask } from '../../utils/fetchFlask';
import {
    formatIsoDate,
    getCurrentMonthStart,
    getDayCountInRange,
    getDaysInMonth
} from '../../utils/dateUtils';
import {
    formatDecimal,
    parseNumberPtBr
} from '../../utils/numberUtils';
import { isBlank } from '../../utils/stringFormat';
import {
    FASE_OPTIONS,
    ORIGEM_OPTIONS,
    STATUS_OPTIONS,
    VENDEDOR_OPTIONS
} from './crmShared';

export const CRM_DASHBOARD_API = 'crm/dashboard';
export const CRM_MARKETING_DATA_API = 'crm/marketingData';
export const CRM_DASHBOARD_TARGET_REVENUE = 400000;
export const CRM_DASHBOARD_MIN_DATE = new Date(2025, 11, 1);
export const DASHBOARD_GROUP_KEYS = ['vendedor_id', 'status_id', 'fase_id', 'origem_id'];
export const EMPTY_MARKETING_DATA = [];
export const EMPTY_DASHBOARD_DATA = {
    vendedor_id: [],
    status_id: [],
    fase_id: [],
    origem_id: [],
    tempo_medio_fase: []
};

const buildOptionLabelMap = (options) => new Map(options.map((option) => [option.id, option.label]));

const statusLabels = buildOptionLabelMap(STATUS_OPTIONS);
const faseLabels = buildOptionLabelMap(FASE_OPTIONS);
const origemLabels = buildOptionLabelMap(ORIGEM_OPTIONS);
const vendedorLabels = buildOptionLabelMap(VENDEDOR_OPTIONS);

const sumGroupValue = (rows, field) => rows.reduce((sum, row) => sum + (
    field === 'ticket' ? parseNumberPtBr(row[field]) : row[field]
), 0);

const buildBreakdown = (rows, field, labelMap) => rows.map((row) => {
    const id = row[field];
    const fallbackLabel = id === null || id === undefined ? 'Não informado' : `ID ${id}`;

    return {
        id,
        label: labelMap.get(id) ?? fallbackLabel,
        count: row.quantidade_leads,
        totalTicket: parseNumberPtBr(row.ticket)
    };
}).sort((a, b) => b.count - a.count);

const buildPhaseBreakdown = (rows) => {
    const countsById = new Map(rows.map((row) => [
        row.fase_id,
        {
            count: row.quantidade_leads,
            totalTicket: parseNumberPtBr(row.ticket)
        }
    ]));

    return FASE_OPTIONS.map((option) => {
        const values = countsById.get(option.id);

        return {
            id: option.id,
            label: faseLabels.get(option.id) ?? option.label,
            count: values?.count ?? 0,
            totalTicket: values?.totalTicket ?? 0
        };
    });
};

const toHoursOrNull = (value) => (isBlank(value) ? null : parseNumberPtBr(value, null));

// Backend contract (SET-116): tempo_medio / tempo_mediano already expressed in hours.
const buildPhaseDurations = (rows) => new Map(rows.map((row) => [
    row.fase_id,
    {
        averageHours: toHoursOrNull(row.tempo_medio),
        medianHours: toHoursOrNull(row.tempo_mediano)
    }
]));

const buildFunnelData = (phaseBreakdown, phaseDurations) => phaseBreakdown.map((phase, index) => {
    const cumulativeRows = phaseBreakdown.slice(index);
    const duration = phaseDurations.get(phase.id);

    return {
        id: phase.id,
        label: phase.label,
        count: sumGroupValue(cumulativeRows, 'count'),
        totalTicket: sumGroupValue(cumulativeRows, 'totalTicket'),
        averageHours: duration?.averageHours ?? null,
        medianHours: duration?.medianHours ?? null
    };
});

export const buildDashboardDateRange = () => ({
    startDate: getCurrentMonthStart(),
    endDate: new Date()
});

export const buildDashboardFilters = () => ({
    vendedor_id: [],
    status_id: [],
    fase_id: [],
    origem_id: []
});

export const buildCrmDashboardPayload = ({ dateRange, filters }) => JSON.stringify({
    data_inicio: formatIsoDate(dateRange.startDate),
    data_fim: formatIsoDate(dateRange.endDate),
    vendedor_id: filters.vendedor_id,
    status_id: filters.status_id,
    fase_id: filters.fase_id,
    origem_id: filters.origem_id
});

export const buildMarketingDataPayload = ({ dateRange }) => JSON.stringify({
    data_inicio: formatIsoDate(dateRange.startDate),
    data_fim: formatIsoDate(dateRange.endDate)
});

export const fetchCrmDashboard = async ({ dateRange, filters }) => {
    const response = await fetchFlask(CRM_DASHBOARD_API, buildCrmDashboardPayload({ dateRange, filters }));
    return response.data;
};

export const fetchMarketingData = async ({ dateRange }) => {
    const response = await fetchFlask(CRM_MARKETING_DATA_API, buildMarketingDataPayload({ dateRange }));
    return response.data;
};

export const buildMarketingCostViewModel = ({ marketingData, selectedOriginIds, wonKwh }) => {
    const selectedRows = selectedOriginIds.length === 0
        ? marketingData
        : marketingData.filter((row) => selectedOriginIds.includes(row.origem_id));
    const totalCost = selectedRows.reduce((sum, row) => sum + row.custo_mes, 0);
    const costPerWonKwh = wonKwh > 0 ? totalCost / wonKwh : 0;

    return {
        costPerWonKwh,
        totalCost
    };
};

export const getWonStage = (funnelData) => funnelData.find((item) => (
    item.label.toLowerCase() === 'conquistado'
)) ?? funnelData.at(-1);

// A meta é mensal, o filtro passou a ser diário (SET-101): a projeção vira o ritmo diário do
// período aplicado ao mês em que ele termina. Para o período padrão (1º do mês até hoje) o
// resultado é o mesmo de antes, quando o divisor era o dia corrente.
export const buildGoalModel = ({ dateRange, wonTicket }) => {
    const daysInEndMonth = getDaysInMonth(dateRange.endDate);
    const rangeDays = getDayCountInRange(dateRange.startDate, dateRange.endDate);
    const projectedRevenue = rangeDays > 0
        ? (wonTicket * daysInEndMonth) / rangeDays
        : 0;

    return {
        target: CRM_DASHBOARD_TARGET_REVENUE,
        wonTicket,
        remainingTicket: Math.max(CRM_DASHBOARD_TARGET_REVENUE - wonTicket, 0),
        projectionRatio: projectedRevenue / CRM_DASHBOARD_TARGET_REVENUE
    };
};

export const buildGoalProjectionText = ({ dateRange, wonTicket }) => {
    const goalModel = buildGoalModel({ dateRange, wonTicket });
    const projectionPercent = formatDecimal(goalModel.projectionRatio * 100);

    return `${projectionPercent}% da meta`;
};

export const buildDashboardViewModel = (dashboardData) => {
    const totalSourceRows = DASHBOARD_GROUP_KEYS
        .map((key) => dashboardData[key])
        .find((rows) => rows.length > 0) ?? [];

    const totalLeads = sumGroupValue(totalSourceRows, 'quantidade_leads');
    const totalTicket = sumGroupValue(totalSourceRows, 'ticket');
    const averageTicket = totalLeads > 0 ? totalTicket / totalLeads : 0;

    const statusBreakdown = buildBreakdown(dashboardData.status_id, 'status_id', statusLabels);
    const originBreakdown = buildBreakdown(dashboardData.origem_id, 'origem_id', origemLabels);
    const sellerBreakdown = buildBreakdown(dashboardData.vendedor_id, 'vendedor_id', vendedorLabels);
    const phaseBreakdown = buildPhaseBreakdown(dashboardData.fase_id);
    const phaseDurations = buildPhaseDurations(dashboardData.tempo_medio_fase ?? []);
    const funnelData = buildFunnelData(phaseBreakdown, phaseDurations);
    const wonPhase = phaseBreakdown.find((item) => item.id === 5);

    return {
        totalLeads,
        totalTicket,
        averageTicket,
        statusBreakdown,
        originBreakdown,
        sellerBreakdown,
        phaseBreakdown,
        funnelData,
        wonCount: wonPhase?.count ?? 0,
        wonKwh: wonPhase?.totalTicket ?? 0,
        maxFunnelValue: Math.max(...funnelData.map((item) => item.count), 1)
    };
};
